import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, CalendarCheck, Menu, User, Heart, Settings, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useWishlistCount } from "@/lib/wishlist";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export const StayHeader = ({ className = "" }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const wishCount = useWishlistCount();

  const link = (to, label, Icon, testid) => {
    const active = pathname === to;
    return (
      <Link
        to={to}
        data-testid={testid}
        aria-label={label}
        title={label}
        className={`inline-flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2 text-sm font-medium transition-colors ${
          active ? "bg-ink text-white" : "text-ink-soft hover:bg-paper-muted"
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">{label}</span>
      </Link>
    );
  };

  return (
    <header className={`sticky top-0 z-40 bg-paper/85 backdrop-blur-xl border-b border-black/5 ${className}`} data-testid="stay-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[70px] flex items-center justify-between gap-3">
        <Link to="/" data-testid="stay-header-logo" className="shrink-0 min-w-0">
          <Logo />
        </Link>
        <nav className="flex items-center gap-1.5 shrink-0">
          {link("/stays", "Stays", Search, "stay-nav-stays")}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                aria-label="Account menu"
                className="relative inline-flex items-center gap-2 rounded-full border border-black/10 bg-white pl-3 pr-1.5 py-1.5 text-ink hover:shadow-sm transition-shadow"
              >
                <Menu className="w-4 h-4" aria-hidden="true" />
                <span className="w-7 h-7 rounded-full bg-ink text-white flex items-center justify-center">
                  <User className="w-4 h-4" aria-hidden="true" />
                </span>
                {wishCount > 0 && (
                  <span data-testid="user-menu-wish-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">
                    {wishCount}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My StayFlow</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist" className="cursor-pointer">
                <Heart className="w-4 h-4 mr-2 text-brand" />
                Wishlist
                {wishCount > 0 && <span className="ml-auto rounded-full bg-brand-soft text-brand-dark text-xs font-semibold px-2 py-0.5">{wishCount}</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/booking/lookup")} data-testid="menu-bookings" className="cursor-pointer">
                <CalendarCheck className="w-4 h-4 mr-2 text-ink-soft" />
                Find my booking
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2 text-ink-soft" />
                Saved details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/pro")} data-testid="menu-pro" className="cursor-pointer">
                <Sparkles className="w-4 h-4 mr-2 text-brand" />
                Plans &amp; pricing
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-ink-faint">For hosts</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
};

export default StayHeader;
